<template>
  <div class="-mx-sm">
    <h1 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-none md:italic md:font-serif md:tracking-tiny mb-9.5 uppercase text-center md:text-left md:normal-case font-bold tracking-lg">
      {{ $t('Dashboard') }}
    </h1>
    <div class="flex flex-wrap">
      <div class="w-full">
        <account-info class="mb-5" />
        <div class="md:px-8.5 px-5 md:py-xl py-5 md:bg-white md:mb-5 rounded-20px">
          <div class="flex items-start">
            <h2 class="text-primary font-normal text-mid leading-none italic font-serif tracking-tiny mb-9.5">
              {{ $t('Address Book') }}
            </h2>
<!--            <router-link :to="localizedRoute({name: 'my-address'})" class="ml-auto text-sm leading-none tracking-lg uppercase underline hover:no-underline">
              {{ $t('Manage Addresses') }}
            </router-link>-->
          </div>
          <div class="flex">
            <div class="w-full md:w-1/2 md:pr-8 pr-2">
              <my-address
                :address="billingAddress"
                is-billing
              />
            </div>
            <div class="w-full md:w-1/2 md:pl-8 pl-2">
              <my-address
                :address="shippingAddress"
                is-shipping
              />
            </div>
          </div>
        </div>
        <div
          v-if="brandManagerName || brandManagerEmail"
          class="md:px-8.5 px-5 md:py-xl py-5 md:bg-white md:mb-5 rounded-20px"
        >
          <h2 class="text-primary font-normal text-mid leading-none italic font-serif tracking-tiny mb-8">
            {{ $t('Your Account Consultant') }}
          </h2>
          <div class="flex flex-wrap items-center">
            <div class="w-full md:w-1/4 mb-5 md:mb-0">
              <img
                v-if="avatarUrl"
                :src="avatarUrl"
                :alt="brandManagerName || $t('Account Consultant')"
                class="rounded-full w-24 h-24 object-cover"
              >
            </div>
            <div class="w-full md:w-3/4">
              <h3
                v-if="brandManagerName"
                class="text-primary font-normal text-base leading-none font-serif tracking-tiny mb-5"
              >
                {{ brandManagerName }}
              </h3>
              <p
                v-if="brandManagerEmail"
                class="leading-h5 text-above-md text-dark tracking-xs mb-5"
              >
                <a
                  :href="`mailto:${brandManagerEmail}`"
                  class="text-brown-2 hover:underline"
                >{{ brandManagerEmail }}</a>
              </p>
              <button
                type="button"
                class="inline-block slow-color-change whitespace-no-wrap outline-none focus:outline-none rounded-0 hover:bg-primary2 border-0 text-white text-center text-h5 uppercase tracking-sm bg-black py-1 px-5"
                @click="showContactForm = !showContactForm"
              >
                {{ showContactForm ? $t('Cancel') : $t('Contact') }}
              </button>
            </div>
          </div>
          <div
            v-if="showContactForm"
            class="w-full mt-8"
          >
            <form @submit.prevent="submitContactForm">
              <base-input
                class="mb-4"
                input-class="mt-1"
                type="text"
                name="subject"
                :placeholder="$t('Subject (optional)')"
                v-model.trim="formData.subject"
              >
                {{ $t('Subject') }}
              </base-input>
              <base-textarea
                class="mb-4"
                input-class="mt-1"
                name="message"
                :rows="5"
                :placeholder="$t('How can we help?')"
                v-model.trim="formData.message"
                :validations="[{
                  condition: submitted && !formData.message,
                  text: $t('Message is required')
                }]"
              >
                <span class="text-md">*</span> {{ $t('Message') }}
              </base-textarea>
              <button
                type="submit"
                class="inline-block slow-color-change whitespace-no-wrap outline-none focus:outline-none rounded-0 hover:bg-primary2 border-0 text-white text-center text-h5 uppercase tracking-sm bg-black py-1 px-5"
                :disabled="isSubmitting || !formData.message"
              >
                {{ isSubmitting ? $t('Sending…') : $t('Send Message') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import i18n from '@vue-storefront/i18n'
import MyOrders from '@vue-storefront/core/compatibility/components/blocks/MyAccount/MyOrders'
import AccountInfo from 'theme/components/core/blocks/MyAccount/AccountInfo'
import AddressBook from 'theme/mixins/blocks/AddressBook'
import MyAddress from './MyAddress'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput'
import BaseTextarea from 'theme/components/core/blocks/Form/BaseTextarea'
import md5 from 'theme/helpers/md5'

export default {
  name: 'MyDashboard',
  components: {
    MyAddress,
    AccountInfo,
    BaseInput,
    BaseTextarea
  },
  mixins: [AddressBook, MyOrders],
  data () {
    return {
      showContactForm: false,
      submitted: false,
      formData: {
        subject: '',
        message: ''
      }
    }
  },
  computed: {
    ...mapGetters({
      brandManagerName: 'netsuite/getBrandManagerName',
      brandManagerEmail: 'netsuite/getBrandManagerEmail',
      isSubmitting: 'netsuite/isSubmitting',
      isLoggedIn: 'user/isLoggedIn'
    }),
    avatarUrl () {
      if (!this.brandManagerEmail) return ''
      const hash = md5(this.brandManagerEmail.trim().toLowerCase())
      return `https://www.gravatar.com/avatar/${hash}?s=192&d=mp`
    }
  },
  mounted () {
    if (this.isLoggedIn) {
      this.$store.dispatch('netsuite/fetchCustomer').catch(() => {})
    }
  },
  methods: {
    async submitContactForm () {
      this.submitted = true
      if (!this.formData.message) return

      try {
        await this.$store.dispatch('netsuite/submitCustomerServiceRequest', {
          subject: this.formData.subject || undefined,
          message: this.formData.message
        })
        this.$store.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('Your message has been sent to your account consultant.'),
          action1: { label: i18n.t('OK') }
        })
        this.formData.subject = ''
        this.formData.message = ''
        this.submitted = false
        this.showContactForm = false
      } catch (err) {
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: i18n.t(err.message || 'Unable to send your message. Please try again later.'),
          action1: { label: i18n.t('OK') }
        })
      }
    }
  }
}
</script>
